package one.healthium.med.presentation.navigation.home.compose

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import ifaandroid.apps.med.composeapp.generated.resources.Res
import ifaandroid.apps.med.composeapp.generated.resources.ic_calendar_clock
import ifaandroid.apps.med.composeapp.generated.resources.ic_home
import ifaandroid.apps.med.composeapp.generated.resources.ic_scroll_content
import ifaandroid.apps.med.composeapp.generated.resources.ic_speechbubble_text
import ifaandroid.apps.med.composeapp.generated.resources.ic_user
import kotlinx.coroutines.launch
import loco.MAIN_NAVIGATION_CHAT_LABEL
import loco.MAIN_NAVIGATION_DAILY_LABEL
import loco.MAIN_NAVIGATION_INSIGHTS_LABEL
import loco.MAIN_NAVIGATION_ME_LABEL
import loco.MAIN_NAVIGATION_TODAY_LABEL
import one.healthium.med.presentation.navigation.home.HomeComponent
import one.healthium.tasks.ui.DailyTasksBadge
import one.healthium.ui.compose.components.AppNavigationBar
import one.healthium.ui.compose.components.AppNavigationBarItem
import one.healthium.ui.compose.components.AppNavigationBarMinHeight
import one.healthium.ui.compose.components.LocalGlobalProgressIndicator
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_17
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
internal fun BottomNavigation(
    component: HomeComponent,
) {
    val stack by component.stack.subscribeAsState()
    val activeComponent = stack.active.instance
    val scope = rememberCoroutineScope()
    val globalProgressIndicator = LocalGlobalProgressIndicator.current

    AppNavigationBar {
        // Today
        AppNavigationBarItem(
            modifier = Modifier.width(AppNavigationBarMinHeight),
            icon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_home),
                    tint = accentColor(activeComponent is HomeComponent.Child.TodayChild),
                    contentDescription = null,
                )
            },
            label = {
                Text(
                    text = stringResource(loco.Res.string.MAIN_NAVIGATION_TODAY_LABEL),
                    color = accentColor(activeComponent is HomeComponent.Child.TodayChild),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            },
            selected = activeComponent is HomeComponent.Child.TodayChild,
            onClick = component::onTodayTabClicked,
        )

        // DailyTasks
        AppNavigationBarItem(
            modifier = Modifier.width(AppNavigationBarMinHeight),
            icon = {
                Box {
                    Icon(
                        painter = painterResource(Res.drawable.ic_calendar_clock),
                        tint = accentColor(activeComponent is HomeComponent.Child.DailyTasksChild),
                        contentDescription = null,
                    )
                    DailyTasksBadge(
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .offset(x = 3.dp, y = (-4).dp)
                    )
                }
            },
            label = {
                Text(
                    text = stringResource(loco.Res.string.MAIN_NAVIGATION_DAILY_LABEL),
                    color = accentColor(activeComponent is HomeComponent.Child.DailyTasksChild),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            },
            selected = activeComponent is HomeComponent.Child.DailyTasksChild,
            onClick = component::onDailyTasksTabClicked,
        )

        // Insights
        AppNavigationBarItem(
            modifier = Modifier.width(AppNavigationBarMinHeight),
            icon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_scroll_content),
                    tint = accentColor(activeComponent is HomeComponent.Child.InsightsChild),
                    contentDescription = null,
                )
            },
            label = {
                Text(
                    text = stringResource(loco.Res.string.MAIN_NAVIGATION_INSIGHTS_LABEL),
                    color = accentColor(activeComponent is HomeComponent.Child.InsightsChild),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            },
            selected = activeComponent is HomeComponent.Child.InsightsChild,
            onClick = component::onInsightsTabClicked,
        )

        // Chat
        AppNavigationBarItem(
            modifier = Modifier.width(AppNavigationBarMinHeight),
            icon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_speechbubble_text),
                    tint = accentColor(activeComponent is HomeComponent.Child.ChatChild),
                    contentDescription = null,
                )
            },
            label = {
                Text(
                    text = stringResource(loco.Res.string.MAIN_NAVIGATION_CHAT_LABEL),
                    color = accentColor(activeComponent is HomeComponent.Child.ChatChild),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            },
            selected = activeComponent is HomeComponent.Child.ChatChild,
            onClick = {
                scope.launch {
                    globalProgressIndicator.value = true
                    component.onChatTabClicked()
                    globalProgressIndicator.value = false
                }
            },
        )

        // Profile
        AppNavigationBarItem(
            modifier = Modifier.width(AppNavigationBarMinHeight),
            icon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_user),
                    tint = accentColor(activeComponent is HomeComponent.Child.ProfileChild),
                    contentDescription = null,
                )
            },
            label = {
                Text(
                    text = stringResource(loco.Res.string.MAIN_NAVIGATION_ME_LABEL),
                    color = accentColor(activeComponent is HomeComponent.Child.ProfileChild),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            },
            selected = activeComponent is HomeComponent.Child.ProfileChild,
            onClick = component::onProfileTabClicked,
        )
    }
}

@Composable
private fun accentColor(active: Boolean) = if (active) {
    AppTheme.colors.primary
} else {
    gray_17
}
